import * as ActionTypes from 'state/types'
import * as Actions from 'state/actions'
import ReconnectingWebSocket from 'reconnecting-websocket'
import * as ArrayUtil from 'shared/utils/array'
import Emitter from 'shared/utils/emitter'

const CONNECTING_READY_STATE = 0
const OPEN_READY_STATE = 1
const CLOSING_READY_STATE = 2
const CLOSED_READY_STATE = 3
const PING_ID_START = 1000

const sockets = {
  collection: [],

  each: (cb) => {
    // console.info('Looping the collection', sockets.collection)
    sockets.collection.forEach((s) => {
      // console.info('DOing a callback on every connection')
      cb(s)
    })
  },

  add: (organisationId, socket) => {
    // console.info('Adding connection for agentId', agentId, sockets.collection)
    if (sockets.collection.find(s => s.organisationId === organisationId)) {
      console.error('Adding socket for organisation that already exists', organisationId)
      return
    }
    sockets.collection.push({
      organisationId,
      socket,
      pingId: PING_ID_START,
      usedPingId: null
    })
  },

  contains: (organisationId) => {
    if (sockets.collection.find(s => s.organisationId === organisationId)) {
      return true
    }
    return false
  },

  remove: (organisationId) => {
    const idx = sockets.collection.findIndex(s => s.organisationId === organisationId)
    if (idx === -1) {
      return
    }
    // console.info('Removing connection for agentId', agentId, idx)
    sockets.collection.splice(idx, 1)
  },

  clear: () => {
    // console.info('Clear all connections')
    sockets.collection = []
  },

  with: (organisationId) => {
    // console.info('FInd socket with organisationId', organisationId)
    const item = sockets.collection.find(s => s.organisationId === organisationId)
    if (!item) {
      return null
    }

    return item.socket
  },

  find: (organisationId) => {
    // console.info('FInd socket with organisationId', organisationId)
    const item = sockets.collection.find(s => s.organisationId === organisationId)
    if (!item) {
      return null
    }

    return item
  },

  empty: () => !sockets.collection.length,

  updateKey: ({organisationId, key, nextValue}) => {
    const index = ArrayUtil.indexOf(sockets.collection, s => s.organisationId === organisationId)

    if (index > -1) {
      sockets.collection[index][key] = nextValue
    }
  }
}

const pingSockets = () => {
  setInterval(() => {

    sockets.each(({socket, pingId, usedPingId, organisationId}) => {
      if (!isSocketConnectionOpen(socket)) {
        //dispatch actions depends on status

        switch (socket.readyState) {
          case CONNECTING_READY_STATE: {
            console.warn('socket is connecting, organisationId', organisationId)
            break
          }

          case CLOSING_READY_STATE: {
            console.warn('socket is closing, organisationId', organisationId)
            break
          }

          case CLOSED_READY_STATE: {
            console.warn('socket is closed, organisationId', organisationId)
            break
          }
        }

        sockets.updateKey({
          organisationId,
          key: 'usedPingId',
          nextValue: null
        })

        return
      }

      if (pingId === usedPingId) {
        //during the 5 secs we didn't receive a pong reply for this pingId
        console.warn('during the 5 secs we didn\'t receive a pong reply for this pingId')
        return
      }

      const enveloppe = {
        type: 'ping',
        payload: {
          to: pingId
        }
      }

      socket.send(JSON.stringify(enveloppe))

      sockets.updateKey({
        organisationId,
        key: 'usedPingId',
        nextValue: pingId
      })
    })

  }, 5000)
}

const handlePongMessage = ({payload, organisationId}) => {
  // console.log('Received PONG message with payload, ', payload)
  const {replyTo} = payload

  const socketItem = sockets.find(organisationId)
  if (!socketItem) {
    console.warn(
      'handlePongMessage :: Cannot find the socket connection for organisationId ',
      organisationId
    )

    return
  }
  if (socketItem.pingId !== replyTo) {
    console.warn('handlePongMessage :: Mismatched pingId with pong reply')

    return
  }

  sockets.updateKey({
    organisationId,
    key: 'pingId',
    nextValue: ++socketItem.pingId
  })
}

const isSocketConnectionOpen = socket => {
  if (!socket) return false
  if (window.navigator.onLine === false) {
    console.warn('socketMiddleware :: the internet connection is offline')
    return false
  }

  return socket.readyState === OPEN_READY_STATE
}

const handleOpen = (socket, store, endpoints, socketCounter) => () => {

  const token = store.getState().session.token

  if (!isSocketConnectionOpen(socket)) return

  setTimeout(() => openNextSocketConnection(store, endpoints, socketCounter), 100)
  // Send auth
  socket.send(JSON.stringify({
    type: 'authenticate',
    payload: {
      token
    }
  }))
}

const handleError = (socket, store, organisationId) => (err) => {
  console.error('Error using websocket', organisationId, err)
  if (socket.retryCount === 19) {
    console.error('Failed to establish a connection after 19 attempt(s)')
    store.dispatch(Actions.socketReconnectionError())
  }
}

const handleMessage = (socket, store, organisationId) => evt => {
  try {
    const {
      type,
      payload
    } = JSON.parse(evt.data)

    switch (type) {
      case 'pong': {
        handlePongMessage({payload, organisationId})
        break
      }

      case 'ping': {
        socket.send(JSON.stringify({
          type: 'pong'
        }))
        break
      }

      case 'auth.check.session': {
        Emitter.emit('auth.check.session')
        break
      }
    }
  } catch (err) {
    console.error('Error while handling a socket message', err)
  }
}


const openNextSocketConnection = (store, endpoints, socketCounter = 0) => {

  if (endpoints.length - 1 < socketCounter) return

  const {
    organisationId,
    socketUrl
  } = endpoints[socketCounter]

  if (sockets.with(organisationId)) {
    return
  }

  const options = {
    //maxReconnectionDelay: 10000,
    minReconnectionDelay: 1000 + Math.random() * 4000,
    reconnectionDelayGrowFactor: 1.3,
    // minUptime: 5000,
    connectionTimeout: 6000,
    maxRetries: 20
    // maxEnqueuedMessages: 10,
    // startClosed: false,
    // debug: false
  }

  const socket = new ReconnectingWebSocket(socketUrl, [], options)
  socket.onmessage = handleMessage(socket, store, organisationId)
  socket.onopen = handleOpen(socket, store, endpoints, socketCounter + 1)
  socket.onerror = handleError(socket, store, organisationId)
  sockets.add(organisationId, socket)
}

export const generalSocketMiddleware = store => next => action => {

  switch (action.type) {
    case `${ActionTypes.GENERAL_SOCKET_INFO_REQUEST}_SUCCESS`: {

      const endpoints = action.response.payload.endpoints
      try {
        openNextSocketConnection(store, endpoints)

        pingSockets()
      } catch (err) {
        console.error('Error, failed to connect with websocket', err)
      }

      break
    }

    case ActionTypes.AUTH_CHECK_SESSION: {
      if(sockets.empty()) {
        console.error('Error, no sockets to send auth event')
        break
      }

      const {
        organisation
      } = store.getState()

      const organisationId = organisation.active.organisationId
      const socket = sockets.with(organisationId)

      if(!isSocketConnectionOpen(socket)) break

      socket.send(JSON.stringify({
        type: 'auth.check.session'
      }))

      break
    }
  }

  return next(action)

}
